import React from 'react';

/**
 * Gemeinsame UI-Bausteine: ein Icon-Set in einem Strichstil (statt Emojis), Seitenkopf,
 * leerer Zustand, Filteranzeige und Tabellen-Stile. Neue Symbole kommen hier dazu.
 */

/* ------------------------------------------------------------------ Icons -- */
export type IconProps = { className?: string; title?: string };
const svg = (path: React.ReactNode): React.FC<IconProps> => {
  const Komponente: React.FC<IconProps> = ({ className = 'h-5 w-5', title }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}>
      {title && <title>{title}</title>}
      {path}
    </svg>
  );
  return Komponente;
};

export const Icon = {
  // Allgemein
  Plus: svg(<path d="M12 5v14M5 12h14" />),
  Search: svg(<><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></>),
  Download: svg(<path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />),
  Upload: svg(<path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />),
  Edit: svg(<path d="M12 20h9M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4 12.5-12.5z" />),
  Trash: svg(<path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m2 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z" />),
  Close: svg(<path d="M18 6L6 18M6 6l12 12" />),
  Check: svg(<path d="M20 6L9 17l-5-5" />),
  Warnung: svg(<><path d="M10.3 3.9L1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z" /><path d="M12 9v4M12 17h.01" /></>),
  Info: svg(<><circle cx="12" cy="12" r="9" /><path d="M12 16v-4M12 8h.01" /></>),
  Eye: svg(<><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" /><circle cx="12" cy="12" r="3" /></>),
  Mehr: svg(<><circle cx="12" cy="5" r="1" /><circle cx="12" cy="12" r="1" /><circle cx="12" cy="19" r="1" /></>),
  Kopieren: svg(<><rect x="9" y="9" width="12" height="12" rx="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" /></>),
  Aktualisieren: svg(<path d="M20.5 12a8.5 8.5 0 11-2.6-6.1L20.5 8.5M20.5 3.5v5h-5" />),
  Drucken: svg(<><path d="M6 9V2h12v7" /><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></>),
  Filter: svg(<path d="M22 3H2l8 9.5V19l4 2v-8.5L22 3z" />),
  Stern: svg(<path d="M12 2.5l2.9 6 6.6.9-4.8 4.6 1.2 6.5L12 17.4l-5.9 3.1 1.2-6.5L2.5 9.4l6.6-.9L12 2.5z" />),
  Telefon: svg(<path d="M22 16.9v3a2 2 0 01-2.2 2 19.8 19.8 0 01-8.6-3.1 19.5 19.5 0 01-6-6A19.8 19.8 0 012.1 4.2 2 2 0 014.1 2h3a2 2 0 012 1.7c.1.9.4 1.8.7 2.7a2 2 0 01-.5 2.1L8 9.8a16 16 0 006 6l1.3-1.3a2 2 0 012.1-.4c.9.3 1.8.6 2.7.7a2 2 0 011.7 2z" />),
  Mail: svg(<><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M22 6l-10 7L2 6" /></>),
  MapPin: svg(<><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0116 0z" /><circle cx="12" cy="10" r="3" /></>),
  Auto: svg(<><path d="M5 17H3v-5l2-5h11l3 5h2v5h-2" /><circle cx="7.5" cy="17" r="2" /><circle cx="16.5" cy="17" r="2" /><path d="M9.5 17h5M3 12h18" /></>),
  Unterschrift: svg(<path d="M3 17c3-3 4-9 6-9s-1 9 2 9 3-4 5-4 1 3 3 3h2M3 21h18" />),
  Kamera: svg(<><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" /><circle cx="12" cy="13" r="4" /></>),
  Bild: svg(<><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></>),
  Notiz: svg(<path d="M21 11.5a8.4 8.4 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.4 8.4 0 01-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.4 8.4 0 013.8-.9h.5a8.5 8.5 0 018 8v.5z" />),
  Glocke: svg(<path d="M18 8a6 6 0 00-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 01-3.4 0" />),
  Kaffee: svg(<path d="M17 8h1a4 4 0 010 8h-1M3 8h14v9a4 4 0 01-4 4H7a4 4 0 01-4-4V8zM6 1v3M10 1v3M14 1v3" />),
  Krank: svg(<><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M12 8v8M8 12h8" /></>),
  Kommen: svg(<path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4M10 17l5-5-5-5M15 12H3" />),
  Vollbild: svg(<path d="M8 3H5a2 2 0 00-2 2v3M21 8V5a2 2 0 00-2-2h-3M16 21h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3" />),
  EyeOff: svg(<path d="M17.9 17.9A10 10 0 0112 20c-7 0-11-8-11-8a18.5 18.5 0 015.1-5.9M9.9 4.2A9 9 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.2 3.2M14.1 14.1a3 3 0 01-4.2-4.2M1 1l22 22" />),
  ArrowLeft: svg(<path d="M19 12H5M11 18l-6-6 6-6" />),
  ArrowRight: svg(<path d="M5 12h14M13 6l6 6-6 6" />),
  ChevronLeft: svg(<path d="M15 18l-6-6 6-6" />),
  ChevronRight: svg(<path d="M9 18l6-6-6-6" />),
  ChevronUp: svg(<path d="M18 15l-6-6-6 6" />),
  ChevronDown: svg(<path d="M6 9l6 6 6-6" />),
  PfeilHoch: svg(<path d="M12 19V5M5 12l7-7 7 7" />),
  PfeilRunter: svg(<path d="M12 5v14M19 12l-7 7-7-7" />),

  // Navigation und Arbeitsbereiche
  Dashboard: svg(<><rect x="3" y="3" width="7" height="9" rx="1.5" /><rect x="14" y="3" width="7" height="5" rx="1.5" /><rect x="14" y="12" width="7" height="9" rx="1.5" /><rect x="3" y="16" width="7" height="5" rx="1.5" /></>),
  Person: svg(<><circle cx="12" cy="8" r="3.5" /><path d="M5 20a7 7 0 0114 0" /></>),
  Kunden: svg(<><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0113 0M16 4.6a3.5 3.5 0 010 6.8M18 14.2a6.5 6.5 0 013.5 5.8" /></>),
  Karte: svg(<path d="M9 4L3 6v14l6-2 6 2 6-2V4l-6 2-6-2zM9 4v14M15 6v14" />),
  Ziel: svg(<><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1" /></>),
  Kalender: svg(<><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></>),
  Bericht: svg(<><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><path d="M14 2v6h6M8 13h8M8 17h5" /></>),
  Protokoll: svg(<><path d="M9 4H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V6a2 2 0 00-2-2h-2" /><rect x="9" y="2" width="6" height="4" rx="1" /><path d="M9 14l2 2 4-4" /></>),
  Grundriss: svg(<><rect x="3" y="5" width="18" height="14" rx="1" /><rect x="7" y="9" width="10" height="6" /><path d="M17 11h2v2h-2" /></>),
  Blitz: svg(<path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" />),
  Paket: svg(<><path d="M21 16V8a2 2 0 00-1-1.7l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.7l7 4a2 2 0 002 0l7-4a2 2 0 001-1.7z" /><path d="M3.3 7L12 12l8.7-5M12 22V12" /></>),
  Aufgaben: svg(<><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" /></>),
  Uhr: svg(<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>),
  Urlaub: svg(<><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></>),
  Diagramm: svg(<path d="M3 3v18h18M7 15l4-4 3 3 6-6" />),
  Regler: svg(<><path d="M4 6h9M17 6h3M4 12h3M11 12h9M4 18h11M19 18h1" /><circle cx="15" cy="6" r="2" /><circle cx="9" cy="12" r="2" /><circle cx="17" cy="18" r="2" /></>),
  Menue: svg(<path d="M4 6.5h16M4 12h16M4 17.5h16" />),
  Raster: svg(<><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></>),
  Werkzeug: svg(<path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.8-3.8a6 6 0 01-7.9 7.9l-6.9 6.9a2.1 2.1 0 01-3-3l6.9-6.9a6 6 0 017.9-7.9l-3.8 3.8z" />),
  Schild: svg(<><path d="M12 21.5s7.5-3.6 7.5-9.5V5.2L12 2.5 4.5 5.2V12c0 5.9 7.5 9.5 7.5 9.5z" /><circle cx="12" cy="10" r="2.3" /><path d="M8.3 16a4.2 4.2 0 017.4 0" /></>),
  Schluessel: svg(<><circle cx="7.5" cy="15.5" r="4" /><path d="M10.4 12.6L20 3M16.5 6.5l2.5 2.5M14 9l2 2" /></>),
  Abmelden: svg(<path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />),
  Tropfen: svg(<path d="M12 2.7l5.7 5.7a8 8 0 11-11.4 0z" />),
};

export type IconName = keyof typeof Icon;

/* ---------------------------------------------------------------- Spinner -- */
export const Spinner: React.FC<{ className?: string }> = ({ className = 'h-5 w-5' }) => (
  <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z" />
  </svg>
);

/** Warten in der Marke: pulsierender Tropfen (Vollbildseiten wie das Stempeln). */
export const TropfenLader: React.FC<{ className?: string }> = ({ className = 'h-14 w-14' }) => (
  <Icon.Tropfen className={`ui-tropfen ${className}`} />
);

/* --------------------------------------------------------- ErfolgsZeichen -- */
/** Inhalte im 24er-Raster der Icons; jede Linie einzeln, damit sie nacheinander gezeichnet werden. */
const ZEICHEN_LINIEN = {
  haken: ['M20 6L9 17l-5-5'],
  kommen: ['M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4', 'M15 12H3', 'M10 17l5-5-5-5'],
};

/**
 * Bestätigung, die sich selbst zeichnet: erst der Kreis, dann das Symbol darin (`ui-zeichnen`).
 * Ohne Bewegungswunsch steht sie sofort fertig da.
 */
export const ErfolgsZeichen: React.FC<{ art?: keyof typeof ZEICHEN_LINIEN; className?: string }> = ({
  art = 'haken', className = 'h-20 w-20',
}) => (
  <svg className={className} viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2.4"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <g className="ui-zeichnen">
      <circle cx="24" cy="24" r="21" pathLength={1} transform="rotate(-90 24 24)" />
    </g>
    <g className="ui-zeichnen" transform="translate(12.5 12) scale(0.95)">
      {ZEICHEN_LINIEN[art].map((d, i) => (
        <path key={d} d={d} pathLength={1} style={{ animationDelay: `${0.35 + i * 0.15}s`, animationDuration: '0.3s' }} />
      ))}
    </g>
  </svg>
);

/* ------------------------------------------------------------ Hochzaehlen -- */
const bewegungReduziert = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Zahl, die beim ersten Erscheinen von 0 hochläuft. Spätere Änderungen (z. B. eine Aufgabe mehr)
 * springen direkt – nur der erste Eindruck bewegt sich.
 */
export const Hochzaehlen: React.FC<{ wert: number; dauerMs?: number }> = ({ wert, dauerMs = 700 }) => {
  const [anzeige, setAnzeige] = React.useState(() => (bewegungReduziert() ? wert : 0));
  const gelaufen = React.useRef(false);

  React.useEffect(() => {
    if (gelaufen.current || wert <= 0 || bewegungReduziert()) {
      setAnzeige(wert);
      if (wert > 0) gelaufen.current = true;
      return;
    }
    const start = performance.now();
    let frame = 0;
    const schritt = (jetzt: number) => {
      const t = Math.min(1, (jetzt - start) / dauerMs);
      setAnzeige(Math.round(wert * (1 - Math.pow(1 - t, 3))));
      if (t < 1) frame = requestAnimationFrame(schritt);
      else gelaufen.current = true;
    };
    frame = requestAnimationFrame(schritt);
    return () => cancelAnimationFrame(frame);
  }, [wert, dauerMs]);

  return <span className="tabular-nums">{anzeige}</span>;
};

/** Ladezustand einer ganzen Seite. */
export const LoadingScreen: React.FC = () => (
  <div className="flex items-center justify-center p-16 text-brand-600">
    <Spinner className="h-10 w-10" />
  </div>
);

/* ------------------------------------------------------------- PageHeader -- */
/**
 * Seitenkopf. Es gibt keinen Header-Balken mehr, jede Seite trägt ihren Titel selbst. Bewusst ohne
 * Eyebrow (gesperrte Großbuchstaben darüber) – die Seitenleiste nennt den Bereich schon.
 * `subtitle` darf eine Gesamtzahl tragen; die hängt nie vom Filter ab.
 */
export const PageHeader: React.FC<{
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}> = ({ title, subtitle, actions, className = '' }) => (
  <div className={`mb-5 flex flex-wrap items-start justify-between gap-3 ${className}`}>
    <div className="min-w-0">
      <h1 className="text-xl font-bold tracking-tight text-gray-900 md:text-2xl">{title}</h1>
      {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
  </div>
);

/* ---------------------------------------------------------------- Filter -- */
/**
 * Ein Filterfeld, das vom Standard abweicht, färbt sich in der Markenfarbe – so sieht man auf einen
 * Blick, *welcher* Filter die Liste einschränkt: `${aktiv ? filterAktiv : ''}`.
 */
export const filterAktiv = '!border-brand-500 !bg-brand-50 !text-brand-900 font-medium';

/**
 * Ohne Filter „24 Kunden“, mit Filter „1 von 24“ plus deutlicher Knopf zum Zurücksetzen.
 */
export const FilterStand: React.FC<{
  treffer: number; gesamt: number; einheit: string; aktiv: boolean; onZuruecksetzen: () => void; className?: string;
}> = ({ treffer, gesamt, einheit, aktiv, onZuruecksetzen, className = '' }) => (
  aktiv ? (
    <span className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500 ${className}`}>
      <span className="whitespace-nowrap tabular-nums">
        <span className="font-semibold text-gray-900">{treffer}</span> von {gesamt}
      </span>
      <button
        type="button"
        onClick={onZuruecksetzen}
        className="inline-flex min-h-[32px] items-center gap-1.5 whitespace-nowrap rounded-full bg-brand-50 px-3 text-sm font-medium text-brand-800 ring-1 ring-brand-200 transition-colors hover:bg-brand-100"
      >
        <Icon.Close className="h-3.5 w-3.5" />Filter zurücksetzen
      </button>
    </span>
  ) : (
    <span className={`whitespace-nowrap text-sm tabular-nums text-gray-500 ${className}`}>{gesamt} {einheit}</span>
  )
);

/** Knopf „Filter zurücksetzen“ für leere Zustände. */
export const FilterZuruecksetzen: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
  >
    <Icon.Close className="h-4 w-4" />Filter zurücksetzen
  </button>
);

/* ------------------------------------------------------------------ Table -- */
// overflow-clip statt -hidden: rundet die Ecken ab, ohne eine eigene Scrollfläche zu bilden – sonst
// blieben die Spaltenköpfe beim Scrollen nicht oben stehen.
export const tableCard = 'overflow-clip rounded-lg border border-gray-200 bg-white shadow-sm';
// Hülle um die Tabelle: auf dem Handy seitlich scrollbar, ab Tablet-Breite ohne eigene Scrollfläche.
export const tableScroll = 'overflow-x-auto md:overflow-x-visible';
// Spaltenköpfe in Satzschreibweise statt gesperrter Großbuchstaben; mitlaufend beim Scrollen.
export const th = 'sticky top-0 z-10 bg-gray-50 px-4 py-3 text-left text-xs font-semibold text-gray-500 shadow-[inset_0_-1px_0_#e5e7eb]';
export const td = 'px-4 py-3 text-sm';
export const tableRow = 'transition-colors hover:bg-gray-50/70';

/* ------------------------------------------------------------- EmptyState -- */
/**
 * Leerer Zustand. Eine leere Liste lädt zum Handeln ein: wo es einen nächsten Schritt gibt, steht er
 * als `action` darunter (z. B. „Kunde anlegen“, bei leerem Filter „Filter zurücksetzen“).
 */
export const EmptyState: React.FC<{ icon?: React.ReactNode; title: string; hint?: string; action?: React.ReactNode; className?: string }> =
  ({ icon, title, hint, action, className = '' }) => (
    <div className={`px-6 py-12 text-center ${className}`}>
      {icon && (
        <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full bg-gray-100 text-gray-400">{icon}</div>
      )}
      <p className="font-medium text-gray-700">{title}</p>
      {hint && <p className="mx-auto mt-1 max-w-md text-sm text-gray-500">{hint}</p>}
      {action && <div className="mt-5 flex justify-center gap-2">{action}</div>}
    </div>
  );

/** Primärer Anlegen-Knopf, wie er in Seitenkopf und leeren Zuständen steht. */
export const AnlegenButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({ children, className = '', ...rest }) => (
  <button
    type="button"
    className={`inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-700 ${className}`}
    {...rest}
  >
    <Icon.Plus className="h-4 w-4" />{children}
  </button>
);

/* ---------------------------------------------------------------- Fehler -- */
/** Fehlermeldung für Toasts: bevorzugt die Meldung des Backends, sonst der Fallback. */
export const fehlerText = (err: unknown, fallback: string): string => {
  const e = err as { response?: { data?: { error?: string; message?: string } } };
  return e?.response?.data?.error || e?.response?.data?.message || fallback;
};
